import React, { useState } from 'react';
import MyInputText from '../../../../components/MyInputText';
import MyInputTextArea from '../../../../components/MyInputTextArea';
import FAQTopic from './FAQTopic';
import { CircularProgress } from '@mui/material';
import useFAQStore from '../../../../stores/FAQStore';
import { type components } from '../../../../endpoint/Schema';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import { type CustomError } from '../../../../models/Error';
import { Toast } from 'primereact/toast';

type IFaq = components['schemas']['FaqDto'];

interface IFAQEditQuestion {
  faq?: IFaq;
  onSave: () => void;
  onCancel?: () => void;
}

export default function FAQEditQuestion(props: IFAQEditQuestion) {
  const tenantId = useTenantStore(selectTenantId);
  const { topics, fetchTopics } = useFAQStore();
  const [question, setQuestion] = useState(props.faq?.question ?? '');
  const [answer, setAnswer] = useState(props.faq?.answer ?? '');
  const [topic, setTopic] = useState(props.faq?.topicId);
  const [pending, setPending] = useState(false);
  const toast = React.useRef<Toast>(null);
  async function handleSave() {
    if (!question || !answer || !topic) return;

    setPending(true);

    try {
      let ok = false;
      if (props.faq?.id) {
        const res = await OpenAPI.PUT('/api/v2/admin/faq/{faqId}', {
          params: {
            path: { faqId: props.faq.id },
            query: { tenantId },
          },
          body: {
            question,
            answer,
            topicId: topic,
            enabled: true,
          },
        });

        ok = res.response.ok;
      } else {
        const res = await OpenAPI.POST('/api/v2/admin/faq', {
          params: { query: { tenantId } },
          body: {
            question,
            answer,
            topicId: topic,
            enabled: true,
          },
        });

        ok = res.response.ok;
      }

      if (ok) {
        try {
          await OpenAPI.PUT('/api/v2/admin/faq/topic/{topicId}/status', {
            params: {
              path: { topicId: topic },
              query: { tenantId },
            },
            body: {
              enabled: true,
            },
          });
        } catch {}

        void fetchTopics();
        props.onSave();
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setPending(false);
  }

  async function handleCancel() {
    setQuestion(props.faq?.question ?? '');
    setAnswer(props.faq?.answer ?? '');
    setTopic(props.faq?.topicId);
    props.onCancel?.();
  }

  return (
    <div
      className={`flex flex-col p-5 rounded-xl ${props.faq ? 'bg-g10 shadow-[0px_4px_12px_rgba(0,0,0,0.1)]' : 'bg-whiteSmoke mt-6'}`}
    >
      <div className="mb-4">{props.faq ? 'Edit question' : 'New question'}</div>

      <MyInputText
        label="Question"
        value={question}
        onChange={(e) => {
          setQuestion(e.target.value);
        }}
        placeholder="What is telematics?"
        inputClassName={`flex flex-1 text-xl ${pending ? 'text-gray' : 'text-black'}`}
        className="my-2"
      />
      <MyInputTextArea
        label="Answer"
        value={answer}
        onChange={(e) => {
          setAnswer(e.target.value);
        }}
        placeholder="Telematics is using your driving data to price your insurance policy. We think your driving behavior tells us a lot of more than the demographic you happen to fall into! Driving data is gathered using GPS. You can tell a surprising amount vabout driving behavior just from your location - such as speed, smoothness, cornering and so on."
        inputClassName={`flex flex-1 text-xl ${pending ? 'text-gray' : 'text-black'}`}
        className="my-4"
        rows={4}
        autoResize
        maxLength={500}
      />

      <span className="text-2xl">Select topic of your question</span>
      <div>
        {topics.map((item, index) => (
          <FAQTopic
            key={`topic_${item.id}_${index}`}
            id={item.id}
            className="mr-4 my-2"
            onClick={() => {
              setTopic(item.id);
            }}
            selected={topic === item.id}
            disabled={pending}
          />
        ))}
      </div>

      <div className="flex gap-5 mt-8">
        <button
          className={`flex justify-center items-center px-16 py-2 ${pending ? 'text-gray' : 'text-black'} rounded-2xl border border-solid border-gray`}
          onClick={handleCancel}
          disabled={pending}
        >
          Cancel
        </button>
        <button
          className={`flex justify-center items-center px-16 py-2 rounded-2xl ${pending ? 'bg-gray' : 'bg-primary'} text-white`}
          onClick={handleSave}
          disabled={pending}
        >
          {pending ? (
            <CircularProgress size="2.5rem" sx={{ color: 'white' }} />
          ) : (
            <div>Save</div>
          )}
        </button>
      </div>
      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}
